import {EnvVariableConfig, JsonPrimitive, VariableByKey, VariableKey} from './types';

const envRegistry = new Map<string, EnvVariableConfig>();
const envCache = new Map<string, JsonPrimitive | undefined>();

/**
 * Define an environment variable for type gen, and then return its value
 */
export function defineEnv<T extends JsonPrimitive = string>(config: EnvVariableConfig<T>) {
  envRegistry.set(config.key, config);
  return {
    ...config,
    value: getEnv(config),
  };
}

export function createVariableCache(variables: Record<string, JsonPrimitive | undefined>) {
  for (const [key, value] of Object.entries(variables)) {
    envCache.set(key, value);
  }
}

/**
 * Get an environment variable value
 */
export function getEnv<Value extends JsonPrimitive = JsonPrimitive>(
  config: EnvVariableConfig<Value>,
): typeof config extends {default: undefined} ? Value | undefined : Value {
  try {
    // Get raw value
    const value = getRawValue(config.key);

    // If no value, use default or throw if required
    if (value === undefined) {
      if (config.default !== undefined) {
        return config.default;
      }

      if (config.required) {
        throw new Error(`Required environment variable ${config.key} is not defined`);
      }

      return undefined as unknown as Value;
    }

    // Apply transform if provided
    if (config.transform) {
      return config.transform(value);
    }

    return value as Value;
  } catch (error) {
    if (config.default !== undefined) {
      return config.default;
    }

    throw error;
  }
}

/**
 * Get the raw value of an environment variable
 */
function getRawValue<Key extends VariableKey>(key: Key): VariableByKey<Key> | undefined {
  // Check cache first
  const cachedValue = envCache.get(key);
  if (cachedValue !== undefined) return cachedValue as VariableByKey<Key>;

  const valueFromBuild = envRegistry.get(key)?.value;
  if (valueFromBuild !== undefined) return valueFromBuild as VariableByKey<Key>;

  // No value found
  return undefined;
}
